import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getProducts } from "../utils/api";
import { imgSrc } from "../utils/images";

function initials(name) {
  const s = String(name || "").trim();
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (a + b).toUpperCase();
}

function prettyRole(role) {
  const v = String(role || "buyer").toLowerCase();
  if (v === "admin") return "Admin";
  if (v === "seller") return "Seller";
  return "Buyer";
}

function formatMaybeDate(val) {
  const s = String(val || "").trim();
  if (!s) return "";
  const d = new Date(s.includes(" ") ? s.replace(" ", "T") : s);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function resolveAvatarSrc(avatarRaw) {
  const a = String(avatarRaw || "").trim();
  if (!a || a.toLowerCase() === "user") return "";
  if (/^https?:\/\//i.test(a)) return a;
  return imgSrc(a);
}

export default function Profile({ currentUser }) {
  // Role
  const role = String(currentUser?.role || "buyer").toLowerCase();
  const isAdmin = role === "admin";
  const isSeller = role === "seller" || isAdmin;
  const isBuyer = role === "buyer" || isAdmin;

  // Seller summary
  const [statsLoading, setStatsLoading] = useState(false);
  const [sellerStats, setSellerStats] = useState({
    total: 0,
    available: 0,
    reserved: 0,
    sold: 0,
    removed: 0,
  });

  // Avatar
  const [avatarOk, setAvatarOk] = useState(true);
  const avatarSrc = resolveAvatarSrc(currentUser?.avatar);
  const showAvatarImg = Boolean(avatarSrc && avatarOk);

  useEffect(() => {
    let cancelled = false;

    async function loadSellerStats() {
      const myId = currentUser?.id ?? currentUser?.userId;
      if (!myId || !isSeller) return;

      try {
        setStatsLoading(true);
        const rows = await getProducts();

        const mine = (Array.isArray(rows) ? rows : [])
          .filter(Boolean)
          .filter(
            (p) =>
              String(p?.seller_user_id ?? p?.user_id ?? "") === String(myId),
          );

        let available = 0;
        let reserved = 0;
        let sold = 0;
        let removed = 0;

        for (let i = 0; i < mine.length; i++) {
          const s = String(mine[i]?.status || "").toLowerCase();
          if (s === "sold") sold += 1;
          else if (s === "reserved") reserved += 1;
          else if (s === "removed") removed += 1;
          else available += 1;
        }

        if (!cancelled) {
          setSellerStats({
            total: mine.length,
            available,
            reserved,
            sold,
            removed,
          });
        }
      } catch {
        if (!cancelled) {
          setSellerStats({
            total: 0,
            available: 0,
            reserved: 0,
            sold: 0,
            removed: 0,
          });
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    }

    loadSellerStats();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isSeller]);

  // Logged out view
  if (!currentUser) {
    return (
      <div className="container pagePadBottom" style={{ maxWidth: 720 }}>
        <div className="card profileHero">
          <div className="profileHeroLeft">
            <div className="profileAvatar profileAvatarGuest">?</div>
            <div>
              <div className="profileTitle">Profile</div>
              <div className="profileSub muted">You are not logged in.</div>
            </div>
          </div>

          <div className="profileActionsRow">
            <Link className="btnPrimary" to="/login">
              Login
            </Link>
            <Link className="btnSecondary" to="/register">
              Register
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // DB fields 
  const displayRole = prettyRole(currentUser?.role);
  const id = currentUser?.id ?? currentUser?.userId ?? "—";
  const name = String(currentUser?.name || "").trim() || "User";
  const email = String(currentUser?.email || "").trim() || "—";
  const contact = String(currentUser?.contact || "").trim() || "—";
  const address = String(currentUser?.address || "").trim() || "—";

  const joined =
    formatMaybeDate(currentUser?.joined) ||
    formatMaybeDate(currentUser?.created_at) ||
    formatMaybeDate(currentUser?.createdAt) ||
    "";

  const createdAt =
    formatMaybeDate(currentUser?.created_at) ||
    formatMaybeDate(currentUser?.createdAt) ||
    "";

  const updatedAt =
    formatMaybeDate(currentUser?.updated_at) ||
    formatMaybeDate(currentUser?.updatedAt) ||
    "";

  return (
    <div
      className="container pagePadBottom profilePage"
      style={{ maxWidth: 980 }}
    >
      {/* HERO */}
      <div className="card profileHero">
        <div className="profileHeroLeft">
          <div className="profileAvatar">
            {showAvatarImg ? (
              <img
                className="profileAvatarImg"
                src={avatarSrc}
                alt={name}
                onError={() => setAvatarOk(false)}
              />
            ) : (
              initials(name)
            )}
          </div>

          <div className="profileHeroText">
            <div className="profileTitleRow">
              <div className="profileTitle">{name}</div>
              <span className="badge profileRoleBadge">
                {displayRole.toUpperCase()}
              </span>
            </div>

            <div className="profileSub muted">
              {email}
              {joined ? <span className="profileDot">•</span> : null}
              {joined ? <span>Joined {joined}</span> : null}
            </div>
          </div>
        </div>

        <div className="profileActionsRow">
          {isBuyer ? (
            <>
              <Link className="btnSecondary" to="/categories">
                Browse shop
              </Link>
              <Link className="btnSecondary" to="/cart">
                View cart
              </Link>
            </>
          ) : null}

          {isSeller ? (
            <>
              <Link className="btnSecondary" to="/my-listings">
                My listings
              </Link>
              <Link className="btnSecondary" to="/requests">
                Buyer requests
              </Link>
            </>
          ) : null}
        </div>
      </div>

      {/* GRID */}
      <div className="profileGrid">
        <div className="card">
          <div className="cardTitleRow">
            <div className="cardTitle">Account details</div>
          </div>

          <div className="divider" />

          <div className="profileInfoGrid">
            <div className="profileInfoItem">
              <div className="label">User ID</div>
              <div className="profileInfoVal">{id}</div>
            </div>

            <div className="profileInfoItem">
              <div className="label">Role</div>
              <div className="profileInfoVal">{displayRole}</div>
            </div>

            <div className="profileInfoItem profileInfoSpan2">
              <div className="label">Email</div>
              <div className="profileInfoVal">{email}</div>
            </div>

            <div className="profileInfoItem">
              <div className="label">Contact</div>
              <div className="profileInfoVal">{contact}</div>
            </div>

            <div className="profileInfoItem profileInfoSpan2">
              <div className="label">Address</div>
              <div className="profileInfoVal profileAddress">{address}</div>
            </div>

            {createdAt ? (
              <div className="profileInfoItem">
                <div className="label">Created</div>
                <div className="profileInfoVal">{createdAt}</div>
              </div>
            ) : null}

            {updatedAt ? (
              <div className="profileInfoItem">
                <div className="label">Updated</div>
                <div className="profileInfoVal">{updatedAt}</div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="card">
          <div className="cardTitleRow">
            <div className="cardTitle">Quick actions</div>
          </div>

          <div className="divider" />

          <div className="profileQuickActions">
            {isBuyer ? (
              <>
                <Link className="btnSecondary" to="/cart">
                  View cart
                </Link>
                <Link className="btnSecondary" to="/orders">
                  Orders
                </Link>
                <Link className="btnSecondary" to="/requests">
                  My requests
                </Link>
              </>
            ) : (
              <>
                <Link className="btnSecondary" to="/my-listings">
                  My listings
                </Link>
                <Link className="btnPrimary" to="/add-item">
                  Add item
                </Link>
                <Link className="btnSecondary" to="/requests">
                  Buyer requests
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {isSeller ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="cardTitleRow">
            <div className="cardTitle">My listings summary</div>
            <span className="muted small">
              {statsLoading ? "Loading…" : `${sellerStats.total} total`}
            </span>
          </div>

          <div className="divider" />

          <div className="profileStatGrid">
            <div className="statCard">
              <div className="statNum">{sellerStats.available}</div>
              <div className="statLabel">Available</div>
            </div>
            <div className="statCard">
              <div className="statNum">{sellerStats.reserved}</div>
              <div className="statLabel">Reserved</div>
            </div>
            <div className="statCard">
              <div className="statNum">{sellerStats.sold}</div>
              <div className="statLabel">Sold</div>
            </div>
            <div className="statCard">
              <div className="statNum">{sellerStats.removed}</div>
              <div className="statLabel">Removed</div>
            </div>
          </div>

          <div className="muted small" style={{ marginTop: 10 }}>
            Tip: “Available” includes items with blank/unknown status.
          </div>
        </div>
      ) : null}
    </div>
  );
}
